import * as React from "react";
import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useGetFieldAdminHSToolsQuery, useSaveFieldAdminHSToolMutation, useSaveFieldAdminHSSectionNotesMutation } from "../fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../../Global/colors";
import { installerLabel } from "../designTokens";
import { HSPageShell, HSSectionLabel, HSNotesBlock, HSFooterActions } from "./HSStepShell";
import { FAErrorCard, FASkeletonRows } from "../FAStates";
import { InstallerAvatar, mergeRosterRows, useTeamRoster } from "./InstallerRoster";
import { FACheckbox, useConfirm } from "../FAControls";
import { FAEmptyState } from "../FALayout";

// The confirmed installer toolkit (NEU-727), verbatim from NeuraDesigns field-admin-app/hs-tools.html.
// Four spellings there are the designer's assumed corrections of the source doc and not yet
// confirmed. Saved inspections keep their own item labels, so changing this list only affects
// inspections started afterwards.
const TOOL_LABELS = [
  "Pliers", "Wire Stripper", "hammer", "Chuck Drill & Key", "Masonry bit 6 inch", "Long Nose Plier",
  "Chisel", "Cable Shear/wire cutter/Stripper", "3m Lead", "Black/Yellow Star Screwdriver",
  "Hellerman Clamp Meter", "6 Screw driver set", "Ryobi Masonry Drill", "Hole Saw 20mm",
  "Headlight with battery", "Flat Screw driver Black/Yellow", "Carry Bag", "Tool Bag", "Twin Plug",
  "Id Card and Phone",
];

function defaultItems() {
  return TOOL_LABELS.map((label) => ({ label, checked: false }));
}

function isDone(record) {
  return record.items.length > 0 && record.items.every((item) => item.checked);
}

// hs-tools.html .acc-status.flag is amber, alongside the green .done / grey .pending.
const STATUS_BADGE_SX = {
  done: { backgroundColor: "rgba(34,197,94,0.14)", color: "#4ADE80" },
  flag: { backgroundColor: "rgba(253,185,64,0.14)", color: AMBER },
  pending: { backgroundColor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)" },
};

const STATUS_BADGE_LABEL = { done: "Done", flag: "Flagged", pending: "Pending" };

export default function Tools() {
  const navigate = useNavigate();
  const { data, isSuccess, isError, refetch } = useGetFieldAdminHSToolsQuery();
  const [saveTool, { isLoading: isSaving }] = useSaveFieldAdminHSToolMutation();
  const [saveSectionNotes] = useSaveFieldAdminHSSectionNotesMutation();

  const { installers, nameById, isRosterReady, isRosterError, refetchRoster } = useTeamRoster();
  const [records, setRecords] = useState(null);
  const [sectionNotes, setSectionNotes] = useState("");
  // The design toggles each accordion independently and ships the first open;
  // a single openId closed the previous one every time.
  const [openIds, setOpenIds] = useState(null);
  const { ask, confirmDialog } = useConfirm();

  // One row per team installer, whether or not a record exists yet — the design's list is
  // the team, not "whoever happens to have a row". Waits for the roster so the list isn't
  // built from records alone and then reshuffled a moment later.
  useEffect(() => {
    if (!isSuccess || !isRosterReady || records !== null) return;
    setSectionNotes(data.notes || "");
    setOpenIds(new Set(installers.length ? [installers[0].id] : []));
    setRecords(mergeRosterRows(installers, data.records, (record, installerId) => ({
      installer_id: installerId,
      // Carried through so saveAll can preserve it. Dropping status here is what let
      // every save recompute done|pending and silently destroy a stored "flag".
      status: record?.status,
      notes: record?.notes || "",
      items: record?.items?.length ? record.items.map((item) => ({ label: item.label, checked: item.checked })) : defaultItems(),
    })));
  }, [isSuccess, data, records, installers, isRosterReady]);

  if (records === null || openIds === null) {
    return (
      <HSPageShell step={{ stepNumber: 2, title: "Tool Inspection", countLabel: "" }}>
        {/* The render is blocked until both this step's data and the roster land, so
            report whichever one failed — reporting only our own left a failing roster
            call showing a shimmer forever, with no message and no way to retry. */}
        {isError || isRosterError
          ? <FAErrorCard onRetry={isRosterError ? refetchRoster : refetch} />
          : <FASkeletonRows count={5} />}
      </HSPageShell>
    );
  }

  const doneCount = records.filter(isDone).length;

  const toggleItem = (installerId, index) => {
    setRecords((prev) => prev.map((r) => (r.installer_id !== installerId ? r : { ...r, items: r.items.map((item, i) => (i === index ? { ...item, checked: !item.checked } : item)) })));
  };

  const updateNotes = (installerId, notes) => {
    setRecords((prev) => prev.map((r) => (r.installer_id !== installerId ? r : { ...r, notes })));
  };

  // "flag" is a deliberate judgement the FA (or IMS) recorded about an installer's kit —
  // it is not derivable from the tick boxes, so it survives a save untouched. Only the
  // done/pending pair is recomputed from the checklist.
  const statusFor = (record) => (record.status === "flag" ? "flag" : isDone(record) ? "done" : "pending");

  // See Attendance.saveAll — returning false keeps HSFooterActions on the page.
  const saveAll = async () => {
    const results = await Promise.all([
      ...records.map((r) => saveTool({ installer_id: r.installer_id, status: statusFor(r), notes: r.notes, items: r.items })),
      saveSectionNotes({ section: "tools", notes: sectionNotes }),
    ]);
    return results.every((result) => !result.error);
  };

  const handleMarkComplete = async () => {
    if (records.length === 0 || doneCount < records.length) {
      const proceed = await ask({
        title: "Complete with installers pending?",
        body: records.length === 0
          ? "No installers on this team's roster have been checked. Mark the tool inspection complete anyway?"
          : `${records.length - doneCount} of ${records.length} installers have unchecked tools. Mark the tool inspection complete anyway?`,
        confirmLabel: "Mark complete",
        cancelLabel: "Keep checking",
        danger: true,
      });
      if (!proceed) return;
    }
    if (!(await saveAll())) return;
    navigate("/field-admin/hs/attendance/");
  };

  return (
    <HSPageShell step={{ stepNumber: 2, title: "Tool Inspection", countLabel: `${doneCount} / ${records.length} installers` }}>

      <HSSectionLabel right={`${records.length} installer${records.length === 1 ? "" : "s"}`}>Per-installer tool check</HSSectionLabel>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {records.length === 0 ? (
          <FAEmptyState py="30px">No installers on this team's roster.</FAEmptyState>
        ) : (
          records.map((record, index) => {
            const open = openIds.has(record.installer_id);
            return (
              <Box key={record.installer_id} sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
                <Box
                  onClick={() => setOpenIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(record.installer_id)) next.delete(record.installer_id); else next.add(record.installer_id);
                    return next;
                  })}
                  sx={{ display: "flex", alignItems: "center", gap: "12px", px: "16px", py: "13px", cursor: "pointer" }}
                >
                  <InstallerAvatar name={nameById.get(record.installer_id)} id={record.installer_id} />
                  <Typography sx={{ flexGrow: 1, color: "#fff", fontWeight: 600, fontSize: "0.88rem" }}>
                    {installerLabel(nameById.get(record.installer_id), record.installer_id)}
                  </Typography>
                  <Box sx={{ fontSize: "0.66rem", fontWeight: 700, px: "8px", py: "3px", borderRadius: "4px", textTransform: "uppercase", ...STATUS_BADGE_SX[statusFor(record)] }}>
                    {STATUS_BADGE_LABEL[statusFor(record)]}
                  </Box>
                  <ExpandMoreIcon sx={{ color: "rgba(255,255,255,0.4)", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                </Box>
                {open ? (
                  <Box sx={{ px: "16px", pb: "16px", pt: "14px", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                    <Box sx={{ display: "grid", // The design switches to 2-up at 720px; MUI's sm is 600 and md is 900, so
                      // neither lands on it — hence the explicit query.
                      gridTemplateColumns: "1fr", "@media (min-width:720px)": { gridTemplateColumns: "1fr 1fr" }, gap: "8px", mb: "12px" }}>
                      {record.items.map((item, i) => (
                        <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: "10px", px: "10px", py: "8px", backgroundColor: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)", borderRadius: "8px" }}>
                          <FACheckbox
                            compact
                            checked={item.checked}
                            onChange={() => toggleItem(record.installer_id, i)}
                            label={item.label}
                            labelSx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.85)" }}
                          />
                        </Box>
                      ))}
                    </Box>
                    <textarea
                      value={record.notes}
                      onChange={(e) => updateNotes(record.installer_id, e.target.value)}
                      placeholder="Per-installer notes…"
                      rows={2}
                      style={{ width: "100%", boxSizing: "border-box", resize: "vertical", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "#0d1117", color: "#fff", padding: "8px 10px", fontSize: "0.8rem" }}
                    />
                  </Box>
                ) : null}
              </Box>
            );
          })
        )}
      </Box>

      <HSNotesBlock
        value={sectionNotes}
        onChange={(e) => setSectionNotes(e.target.value)}
        sub="Shared observations across the team — missing/borrowed kit, training gaps, replacements needed."
        placeholder="e.g. Two multimeters due for re-calibration this month. Apprentice still using shared insulated set — request a dedicated kit from IMS."
      />

      <HSFooterActions onSaveExit={saveAll} onMarkComplete={handleMarkComplete} markCompleteDisabled={isSaving} />
      {confirmDialog}
    </HSPageShell>
  );
}
